// @ts-expect-error
import { Transport } from '@jitsi/js-utils/transport';
import { AGENT_PORTS, AGENT_REQUESTS, EVENTS, REMOTE_CONTROL_MESSAGE_NAME } from './constants';
import logger from './logger';
/**
 * Where the pairing token for an origin is kept. The token is not a secret from
 * the person sitting at this machine — it is the receipt for an approval they
 * gave in person — so localStorage is the right place for it: same origin, same
 * browser, survives a reload, and is discarded when they clear site data.
 */
const TOKEN_STORAGE_KEY = 'kamomeet.remoteControl.agentToken';
/**
 * How long to wait for a loopback port to answer before moving to the next one.
 * A port with nothing behind it refuses immediately; this budget is for the rare
 * case of something that accepts and then says nothing.
 */
const CONNECT_TIMEOUT = 1500;
/**
 * How long an ordinary request may wait for its answer.
 */
const REQUEST_TIMEOUT = 10000;
/**
 * How long the pairing request may wait. This one is a person on the other end,
 * reading a dialog on their own desktop and deciding.
 */
const PAIRING_TIMEOUT = 120000;
/**
 * How often to prove the tab is still alive while a session is running. The
 * agent releases everything it is holding if two of these go missing, so this
 * is what stops a crashed tab leaving a key pressed on someone's keyboard.
 */
const HEARTBEAT_INTERVAL = 1000;
/**
 * Implements the @jitsi/js-utils transport backend over a loopback WebSocket.
 *
 * The upstream receiver was written against a transport whose other end is a
 * host application. Presenting the agent through the same interface is what
 * lets every line of that receiver work unchanged.
 */
class LocalAgentTransportBackend {
    _socket;
    _receiveCallback;
    /**
     * Wraps an already-open socket.
     *
     * @param {WebSocket} socket - The open loopback socket.
     */
    constructor(socket) {
        this._socket = socket;
        this._receiveCallback = () => {
            // Nothing to do until Transport sets its callback.
        };
        // The onmessage property rather than addEventListener: @types/ws is in
        // this project's dependency tree and its overloads collide with the DOM
        // ones under ts-loader, in a way plain tsc does not reproduce.
        this._socket.onmessage = ({ data }) => {
            let message;
            try {
                message = JSON.parse(data);
            }
            catch (error) {
                logger.warn('Remote control agent sent something unreadable', error);
                return;
            }
            this._receiveCallback(message);
        };
    }
    /**
     * Sets the callback Transport uses to receive messages.
     *
     * @param {Function} callback - The callback.
     * @returns {void}
     */
    setReceiveCallback(callback) {
        this._receiveCallback = callback;
    }
    /**
     * Sends one message.
     *
     * @param {Object} message - The enveloped message.
     * @returns {void}
     */
    send(message) {
        if (this._socket.readyState !== WebSocket.OPEN) {
            return;
        }
        this._socket.send(JSON.stringify(message));
    }
    /**
     * Closes the socket.
     *
     * @returns {void}
     */
    dispose() {
        this._socket.close();
    }
}
/**
 * Opens a socket to one loopback port, or gives up.
 *
 * @param {number} port - The port to try.
 * @returns {Promise<WebSocket>}
 */
function openPort(port) {
    return new Promise((resolve, reject) => {
        let socket;
        try {
            // ws:// to 127.0.0.1 is permitted from an https page because
            // loopback is a potentially trustworthy origin. Safari does not
            // implement that exemption, which is why being controlled from
            // Safari is unsupported and said so plainly in the UI.
            socket = new WebSocket(`ws://127.0.0.1:${port}/`);
        }
        catch (error) {
            reject(error);
            return;
        }
        const timeout = setTimeout(() => {
            socket.close();
            reject(new Error(`Timed out connecting to the control agent on port ${port}`));
        }, CONNECT_TIMEOUT);
        socket.onopen = () => {
            clearTimeout(timeout);
            socket.onerror = null;
            resolve(socket);
        };
        socket.onerror = () => {
            clearTimeout(timeout);
            reject(new Error(`No control agent on port ${port}`));
        };
    });
}
/**
 * Finds the control agent on this machine, if it is running.
 *
 * The ports are tried one at a time rather than all at once: a browser that
 * cannot reach loopback at all should produce one quiet failure, not ten
 * simultaneous console errors.
 *
 * @returns {Promise<WebSocket|undefined>}
 */
async function findAgent() {
    for (const port of AGENT_PORTS) {
        try {
            return await openPort(port);
        }
        catch (error) {
            // Nothing there. Try the next one.
        }
    }
    return undefined;
}
/**
 * Reads the pairing token this origin was given, if it has one.
 *
 * @returns {string}
 */
function readToken() {
    try {
        return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
    }
    catch (error) {
        // Storage can be unavailable in a hardened profile. Pairing simply
        // happens again next time.
        return '';
    }
}
/**
 * Remembers the pairing token this origin was given.
 *
 * @param {string} token - The token.
 * @returns {void}
 */
function writeToken(token) {
    try {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
    catch (error) {
        logger.warn('Could not remember the remote control pairing token', error);
    }
}
/**
 * Connects to the control agent on this machine and completes its handshake.
 *
 * @returns {Promise<IAgentConnection|undefined>} The connection, or undefined
 * when no agent is installed.
 */
export async function connectToAgent() {
    const socket = await findAgent();
    if (!socket) {
        return undefined;
    }
    const transport = new Transport({ backend: new LocalAgentTransportBackend(socket) });
    const request = (payload, timeout = REQUEST_TIMEOUT) => {
        let timer;
        return Promise.race([
            transport.sendRequest({ name: REMOTE_CONTROL_MESSAGE_NAME, ...payload }),
            new Promise((_, reject) => {
                timer = window.setTimeout(() => reject(new Error('The control agent did not answer')), timeout);
            })
        ]).finally(() => window.clearTimeout(timer));
    };
    let hello;
    try {
        hello = await request({
            type: AGENT_REQUESTS.hello,
            token: readToken()
        });
    }
    catch (error) {
        logger.warn('The control agent did not complete its handshake', error);
        transport.dispose();
        return undefined;
    }
    // The heartbeat is the dead-man switch: the agent releases every key and
    // button it is holding if two of these go missing, which is what stops a
    // crashed tab leaving a modifier pressed on somebody's keyboard.
    const heartbeat = window.setInterval(() => {
        request({ type: AGENT_REQUESTS.ping }).catch(() => {
            // A missed beat is the agent's problem to act on, not ours.
        });
    }, HEARTBEAT_INTERVAL);
    socket.onclose = () => window.clearInterval(heartbeat);
    return {
        hello,
        transport,
        close: () => {
            window.clearInterval(heartbeat);
            transport.dispose();
        },
        pair: async () => {
            const result = await request({ type: AGENT_REQUESTS.pair }, PAIRING_TIMEOUT);
            if (result?.token) {
                writeToken(result.token);
            }
            // Noted on the connection handle, not in redux: it saves a
            // needless round-trip if control is granted twice in one meeting.
            hello.paired = true;
        }
    };
}
/**
 * Asks the agent to start a session on a chosen display.
 *
 * @param {Object} connection - The agent connection.
 * @param {IAgentDisplay} [screen] - The display the browser is sharing, when it
 * could be worked out.
 * @param {Object} size - The captured track's dimensions, which is what the
 * agent falls back to when it cannot enumerate displays at all.
 * @returns {Promise<Object>}
 */
export function startAgentSession(connection, screen, size) {
    return connection.transport.sendRequest({
        name: REMOTE_CONTROL_MESSAGE_NAME,
        type: AGENT_REQUESTS.start,
        displayId: screen?.id ?? '',
        width: size.width,
        height: size.height
    });
}
/**
 * Tells the agent a session is over.
 *
 * @param {Object} connection - The agent connection.
 * @returns {void}
 */
export function stopAgentSession(connection) {
    connection.transport.sendEvent({
        name: REMOTE_CONTROL_MESSAGE_NAME,
        type: EVENTS.stop
    });
}
